/**
 * In-app notification contracts (project brief §21, `GET/POST /notifications*`).
 *
 * A `Notification` row's `userId` is its recipient — the only actor ever
 * authorized to read or acknowledge it. Every endpoint in
 * `apps/api/src/modules/notifications/**` filters on
 * `userId: actor.actorId` before anything else and returns a uniform 404 for
 * another user's notification id, exactly the same "existence oracle" reasoning
 * `AppError.notVisible` documents for tickets.
 */
import { z } from 'zod';
import { queryBoolean } from './query-params.js';
export const NOTIFICATION_CHANNELS = ['EMAIL', 'IN_APP', 'WEBHOOK', 'TEAMS', 'PUSH'];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
/**
 * The event types a preference row may name. Kept in sync BY HAND with the
 * `eventType` string literals `apps/worker/src/processors/handlers/**` write
 * — there is no shared enum for it at the Prisma layer (the column is a plain
 * string, see schema.prisma `Notification.eventType`).
 */
export const NOTIFICATION_EVENT_TYPES = [
    'TICKET_CREATED',
    'TICKET_ASSIGNED',
    'TICKET_ESCALATED',
    'TICKET_CANCELLED',
    'TICKET_RESTORED',
    'TICKET_CLOSED',
    'TICKET_REOPENED',
    'COMMENT_CREATED',
    'ATTACHMENT_ADDED',
    'ATTACHMENT_REMOVED',
    'RESOLUTION_READY',
    'USER_CONFIRMATION_REQUESTED',
    'TICKET_RESOLUTION_REJECTED',
    'SLA_WARNING',
];
export const notificationEventTypeSchema = z.enum(NOTIFICATION_EVENT_TYPES);
/**
 * Event types a recipient may NOT opt out of, for either channel, because
 * they carry a two-sided-confirmation or security obligation (project brief
 * §14/§25): a user who never learns their resolution needs confirming, or
 * that their rejection reached the team, could never exercise those rights.
 * `PUT /notifications/preferences` rejects a write that tries to disable one
 * of these — see `notifications.service.ts`.
 */
export const NON_OPTIONAL_NOTIFICATION_EVENTS = [
    'USER_CONFIRMATION_REQUESTED',
    'RESOLUTION_READY',
    'TICKET_RESOLUTION_REJECTED',
];
export const notificationDtoSchema = z.object({
    id: z.string().uuid(),
    ticketId: z.string().uuid().nullable(),
    /** Design Workflow deep link (null for ticket notifications). */
    designRequestId: z.string().uuid().nullable(),
    eventType: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const notificationListQueryDtoSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    unreadOnly: queryBoolean.optional(),
});
export const notificationUnreadCountDtoSchema = z.object({ count: z.number().int().nonnegative() });
export const notificationPreferenceDtoSchema = z.object({
    eventType: notificationEventTypeSchema,
    channel: notificationChannelSchema,
    enabled: z.boolean(),
    /** UX only: the client greys out the toggle for a non-optional event/channel pair rather than letting the user flip it and find out from a 422. */
    editable: z.boolean(),
});
export const notificationPreferencesResponseDtoSchema = z.object({
    preferences: z.array(notificationPreferenceDtoSchema),
});
export const updateNotificationPreferencesDtoSchema = z.object({
    preferences: z
        .array(z.object({
        eventType: notificationEventTypeSchema,
        channel: notificationChannelSchema,
        enabled: z.boolean(),
    }))
        .min(1)
        .max(200),
});
//# sourceMappingURL=notification-dto.js.map